import "./Skeleton.css";

/**
 * A single shimmering placeholder block. Purely decorative (`aria-hidden`) —
 * the accessible "loading" announcement lives on the composed skeleton's own
 * `role="status"` wrapper (see `DetailPageSkeleton`), not on each block.
 * Shimmer honors `prefers-reduced-motion` via the app-wide rule in
 * `global.css` (every animation's duration collapses to ~0 for that
 * preference), so there's nothing extra to opt out of here.
 */
export function Skeleton({ className }: { className?: string }) {
  return <div className={["skeleton", className].filter(Boolean).join(" ")} aria-hidden="true" />;
}

/**
 * The shared "this detail page is loading" shape for `ServiceDetailPage`,
 * `HostDetailPage`, and `ProjectDetailPage` (UX polish wave 3) — a header bar
 * plus N card blocks roughly matching each page's real layout, so the page
 * doesn't visually pop in once data arrives (perceived performance). `cards`
 * lets each caller match its own card count without a bespoke skeleton per
 * page. Carries the same accessible name (`"Loading…"`) `LoadingState` used,
 * via `role="status"`, so a screen-reader user gets the same announcement
 * either way.
 */
export function DetailPageSkeleton({ cards = 2 }: { cards?: number }) {
  return (
    <div className="detail-page-skeleton" role="status" aria-label="Loading…">
      <div className="detail-page-skeleton__header">
        <Skeleton className="detail-page-skeleton__title" />
        <Skeleton className="detail-page-skeleton__badge" />
      </div>
      <div className="detail-page-skeleton__cards">
        {Array.from({ length: cards }, (_, index) => (
          <div key={index} className="detail-page-skeleton__card">
            <Skeleton className="detail-page-skeleton__card-title" />
            <Skeleton className="detail-page-skeleton__card-line" />
            <Skeleton className="detail-page-skeleton__card-line detail-page-skeleton__card-line--short" />
          </div>
        ))}
      </div>
    </div>
  );
}
